import type {
  IIntegrationDefinition,
  TIntegrationModalKey,
} from "../constants/integration";
import type { EIntegrationConnectResolution } from "../types/chat";
import type {
  EConnectorType,
  EIntegrationProvider,
  IConnectIntegrationPayload,
  IEnabledIntegrationProviderInfo,
} from "../types/integration";

export enum EIntegrationConnectActionKind {
  // NOTE: Start the connect flow straight away (OAuth redirect / hosted
  // Connect URL) with the payload below — the same POST /integration/access
  // the Integrations page issues.
  CONNECT = "connect",
  // NOTE: More than one connector rail is available; the app must open the
  // SelectConnectorModal and connect with the chosen rail.
  PICK_CONNECTOR = "pick_connector",
  // NOTE: NSAI-5681 — the provider needs a credential form (API key, MCP URL,
  // EWS host, ...). The app opens that provider's own form — the SAME modal the
  // Integrations page opens for it, identified by `modalKey` — right here in
  // the chat, so the card completes where the user is instead of bouncing them
  // to another page and losing the conversation.
  OPEN_MODAL = "open_modal",
  // NOTE: Not connectable from this client's state (disabled, unknown, or a
  // credential form this client does not implement) — take the user to the
  // Integrations page, which shows the truth.
  OPEN_INTEGRATIONS = "open_integrations",
}

export interface IIntegrationConnectActionConnect {
  kind: EIntegrationConnectActionKind.CONNECT;
  payload: Omit<IConnectIntegrationPayload, "redirectTo">;
}

export interface IIntegrationConnectActionPickConnector {
  kind: EIntegrationConnectActionKind.PICK_CONNECTOR;
  provider: EIntegrationProvider;
  availableConnectors: EConnectorType[];
}

export interface IIntegrationConnectActionOpenModal {
  kind: EIntegrationConnectActionKind.OPEN_MODAL;
  modalKey: TIntegrationModalKey;
}

export interface IIntegrationConnectActionOpenIntegrations {
  kind: EIntegrationConnectActionKind.OPEN_INTEGRATIONS;
}

export type TIntegrationConnectAction =
  | IIntegrationConnectActionConnect
  | IIntegrationConnectActionPickConnector
  | IIntegrationConnectActionOpenModal
  | IIntegrationConnectActionOpenIntegrations;

export interface IResolveIntegrationConnectActionProps {
  provider: string;
  providerInfo: IEnabledIntegrationProviderInfo | undefined;
  isExternalIntegrationsProviderEnabled: boolean;
  definition?: IIntegrationDefinition | undefined;
}

export interface IBuildConnectPayloadForDefinitionProps {
  definition: IIntegrationDefinition;
  isExternalIntegrationsProviderEnabled: boolean;
  connectorType?: EConnectorType | undefined;
}

// NOTE: NSAI-5681 — the i18n key for the card's status line, as an enum so both
// apps render the same three states from one place instead of each hand-writing
// key strings. Values are the `chat` namespace keys.
export enum EIntegrationConnectStatusKey {
  // NOTE: A connection for this app arrived AFTER the card was raised, so the
  // card has been answered: the user still has to resend the request, because
  // the turn that raised it is finished.
  CONNECTED = "integration_connect.connected",
  // NOTE: The user came back from the provider's screen without a new
  // connection — a rejected consent, a failed callback, or an abandoned tab.
  // The only resolution that still offers another attempt.
  FAILED = "integration_connect.failed",
  // NOTE: Closed by the user.
  DISMISSED = "integration_connect.dismissed",
  // NOTE: Left behind by a later message in the chat.
  TERMINATED = "integration_connect.terminated",
  // NOTE: The app was already connected when the card was raised — the card
  // stands for a missing ACCOUNT, and the user is being offered another one.
  DESCRIPTION_ANOTHER = "integration_connect.description_another",
  // NOTE: Nothing connected for this app yet.
  DESCRIPTION = "integration_connect.description",
}

export interface IResolveIntegrationConnectStatusKeyProps {
  // NOTE: The recorded outcome, absent while the card is still open. Written by
  // the server at OAuth-callback time — the client never judges the outcome,
  // because a browser-side verdict would be forgeable.
  resolution?: EIntegrationConnectResolution | undefined;
  hasExistingConnection: boolean;
  // NOTE: A newer user message sits below this card in the transcript, so the
  // conversation has moved past it. Read from the rendered ORDER rather than
  // from timestamps: the just-sent message is stamped by the browser's clock,
  // and comparing that against a server-issued one is a race dressed up as a
  // comparison. The server records TERMINATED for the same reason as the
  // message is persisted; deriving it here means the card stops offering an
  // action the moment the user sends, without waiting for that record.
  isSuperseded?: boolean;
}

// NOTE: NSAI-5681 — see canSupersedeIntegrationConnectResolution. Mirrors the
// server's props of the same shape so the two rules read identically.
export interface ICanSupersedeIntegrationConnectResolutionProps {
  current?: EIntegrationConnectResolution | undefined;
  next: EIntegrationConnectResolution;
}

export interface IIsIntegrationConnectPromptActionableProps {
  resolution?: EIntegrationConnectResolution | undefined;
  isSuperseded?: boolean;
}
